import React, { PropsWithChildren } from 'react';
import { COLORS, computeRemSize } from '@ydistri/theme';
import {
  CategoryConfigurationDefinitionResponse,
  FieldConfigurationRequest,
  SkuType,
} from '@ydistri/api-sdk';
import { CSSProperties, styled } from 'styled-components';
import ConfigurationField from '../../../../components/global/ConfigurationField/ConfigurationField';
import { List } from 'antd';
import ConfigurationRowTitle from './ConfigurationRowTitle';
import { computeConfigurationRowLayout } from '../configurationUtils';
import { SpecialConfigurationSettings } from './ConfigurationEntityLists/SpecialOptionDeleteButton';
import useIsConfigurationEditable from '../../../../hooks/useIsConfigurationEditable';

export const ConfigurationRowWrapper = styled(List.Item)<{
  $skuListExcluded?: boolean;
}>`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  justify-content: space-between;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-right: 0 !important;
  flex-wrap: wrap;
  font-size: 1rem;
  min-height: ${computeRemSize(30)};
  ${p => p.$skuListExcluded && `background-color: ${COLORS.BAR_DEADSTOCK_FADED};`}

  & .hover-display {
    display: none;
  }

  & .hover-hide {
    display: block;
  }

  &:hover {
    & .hover-display {
      display: block;
    }
    & .hover-hide {
      display: none;
    }
    background-color: ${p =>
      p.$skuListExcluded ? COLORS.BAR_DEADSTOCK_FADED : COLORS.HOVER_GREY_LIGHT};
  }
`;

export interface ConfigurationFieldsWrapperProps {
  $justifyContent?: string;
  $width?: CSSProperties['width'];
}

const CONFIGURATIONS_FIELD_WRAPPER_WIDTH = 360;
export const ConfigurationFieldsWrapper = styled.div<ConfigurationFieldsWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: ${p => p.$justifyContent ?? 'flex-end'};
  ${p => {
    const width = p.$width ?? computeRemSize(CONFIGURATIONS_FIELD_WRAPPER_WIDTH);
    return `
      width: ${width};
      max-width: ${width};
      min-width: ${width};
    `;
  }}
  height: 100%;
`;

export const ConfigurationFieldsWrapperCentered = styled(ConfigurationFieldsWrapper)`
  justify-content: center;
`;

const CONFIGURATIONS_FIELD_TARGET_WRAPPER_WIDTH = 200;
export const ConfigurationFieldsWrapperTarget = styled(
  ConfigurationFieldsWrapperCentered,
)<ConfigurationFieldsWrapperProps>`
  ${p => {
    if (p.$justifyContent) {
      return `
      justify-content: ${p.$justifyContent};
     `;
    }
  }};
  ${p => {
    const width = p.$width ?? computeRemSize(CONFIGURATIONS_FIELD_TARGET_WRAPPER_WIDTH);
    return `
      width: ${width};
      max-width: ${width};
      min-width: ${width};
    `;
  }}
`;

export type ConfigurationRowLayout = 'skuTypes' | 'single' | 'children';

interface ConfigurationRowProps extends PropsWithChildren {
  definition: CategoryConfigurationDefinitionResponse;
  layout?: ConfigurationRowLayout;
  forcedTitle?: string;
  forcedCategoryId?: number;
  specialConfigurationSettings?: SpecialConfigurationSettings;
  titleDisabled?: boolean;
  controlsDisabled?: boolean;
  onBeforeSave?: (categoryId: number, payload: FieldConfigurationRequest[]) => void;
  onAfterSave?: (categoryId: number, payload: FieldConfigurationRequest[]) => void;
  childrenJustifyContent?: CSSProperties['justifyContent'];
  controlsColumnWidth?: CSSProperties['width'];
}

const ConfigurationRow: React.FC<ConfigurationRowProps> = ({
  definition,
  layout,
  forcedTitle,
  forcedCategoryId,
  specialConfigurationSettings,
  children,
  titleDisabled = false,
  controlsDisabled = false,
  onBeforeSave,
  onAfterSave,
  childrenJustifyContent,
  controlsColumnWidth,
}) => {
  const isConfigurationEditable = useIsConfigurationEditable();
  const fieldsDisabled = controlsDisabled || !isConfigurationEditable;

  const effectiveLayout: ConfigurationRowLayout =
    layout ?? computeConfigurationRowLayout(definition);

  return (
    <ConfigurationRowWrapper>
      <ConfigurationRowTitle
        specialSkuListInfo={specialConfigurationSettings}
        definition={definition}
        forcedTitle={forcedTitle}
        disabled={titleDisabled}
      />
      {effectiveLayout === 'skuTypes' && (
        <ConfigurationFieldsWrapperCentered>
          <ConfigurationField
            skuTypeColumn={SkuType.DeadStock}
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={fieldsDisabled}
            forcedCategoryId={forcedCategoryId}
            onBeforeSave={onBeforeSave}
            onAfterSave={onAfterSave}
          />
          <ConfigurationField
            skuTypeColumn={SkuType.SlowMover}
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={fieldsDisabled}
            forcedCategoryId={forcedCategoryId}
            onBeforeSave={onBeforeSave}
            onAfterSave={onAfterSave}
          />
          <ConfigurationField
            skuTypeColumn={SkuType.FastMover}
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={fieldsDisabled}
            forcedCategoryId={forcedCategoryId}
            onBeforeSave={onBeforeSave}
            onAfterSave={onAfterSave}
          />
        </ConfigurationFieldsWrapperCentered>
      )}
      {effectiveLayout === 'single' && (
        <ConfigurationFieldsWrapperTarget
          $width={controlsColumnWidth}
          $justifyContent={childrenJustifyContent}
        >
          <ConfigurationField
            skuTypeColumn="Target"
            fieldType={definition.fieldType}
            dataType={definition.dataType}
            disabled={fieldsDisabled}
            inputWidth={computeRemSize(150)}
            forcedCategoryId={forcedCategoryId}
            onBeforeSave={onBeforeSave}
            onAfterSave={onAfterSave}
          />
        </ConfigurationFieldsWrapperTarget>
      )}
      {effectiveLayout === 'children' && (
        <ConfigurationFieldsWrapper
          $width={controlsColumnWidth}
          data-testid="ConfigurationFieldsWrapper"
          $justifyContent={childrenJustifyContent}
        >
          {children}
        </ConfigurationFieldsWrapper>
      )}
    </ConfigurationRowWrapper>
  );
};

export default ConfigurationRow;
